'use client';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  useTheme,
} from '@mui/material';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { trackEvent } from '@/lib/analytics/gtm';
import { NestedMenu, NestedMenuItem } from '@/lib/queries/Menus';

import { getDisplayText, getMenuItemUrl, hasSubmenu, isMenuItemActive } from './MenuHelpers';

interface FooterLinksProps {
  footerMenu?: NestedMenu;
  showLabel?: boolean;
  className?: string;
}

const FooterMenuItem = ({ item, depth = 0 }: { item: NestedMenuItem; depth?: number }) => {
  const theme = useTheme();
  const currentPath = usePathname();
  const text = getDisplayText(item);
  const url = getMenuItemUrl(item);
  const itemHasSubmenu = hasSubmenu(item);
  const isActive = isMenuItemActive(item, currentPath);

  if (itemHasSubmenu) {
    const submenuContent = item.SubMenu?.MenuItems?.map((subItem, index) => (
      <FooterMenuItem
        key={`footer-sub-${subItem.__typename}-${index}`}
        item={subItem}
        depth={depth + 1}
      />
    ));

    return (
      <Box
        key={`footer-${item.__typename}-${depth}`}
        mb={1.5}
        sx={{
          fontSize: theme.typography.medium.fontSize,
        }}
      >
        {/* Mobile accordion view (sm and below) */}
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <Accordion
            defaultExpanded={false}
            disableGutters
            elevation={0}
            sx={{
              backgroundColor: 'transparent',
              color: 'white',
              '&:before': {
                display: 'none',
              },
              mb: 0,
              pb: 0,
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: 'purple.200', opacity: 0.5 }} />}
              sx={{
                minHeight: 'auto',
                px: 0,
                py: 0,
                '& .MuiAccordionSummary-content': {
                  my: 0,
                  mb: 2,
                },
              }}
            >
              <Typography
                fontWeight="bold"
                sx={{
                  color: 'purple.200',
                  opacity: '0.5',
                  fontSize: theme.typography.h5.fontSize,
                }}
              >
                {text}
              </Typography>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                px: 0,
                py: 0,
                pb: 1,
              }}
            >
              <Box>{submenuContent}</Box>
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Desktop expanded view (md and above) */}
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <Typography
            fontWeight="bold"
            sx={{
              display: 'inline-block',
              color: 'purple.200',
              opacity: '0.5',
              mb: 2,
              mt: 3,
              fontSize: theme.typography.h5.fontSize,
            }}
          >
            {text}
          </Typography>
          {item.SubMenu?.MenuItems && <Box>{submenuContent}</Box>}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      key={`footer-${item.__typename}-${depth}`}
      mb={1}
      sx={{
        'a:hover,a:focus': {
          opacity: 1,
          textDecoration: 'underline',
        },
      }}
    >
      <Link
        href={url}
        style={{
          color: 'white',
          textDecoration: 'none',
          opacity: isActive ? 1 : 0.8,
        }}
        onClick={() =>
          trackEvent({
            feature: 'navigation',
            action: 'nav_footer_click',
            params: { link_text: text, link_href: url },
          })
        }
      >
        <Typography
          variant="body2"
          sx={{
            color: 'white',
            fontSize: theme.typography.h6.fontSize,
            mb: 1,
            '&:hover': {
              opacity: 1,
              textDecoration: 'underline',
            },
          }}
        >
          {text}
        </Typography>
      </Link>
    </Box>
  );
};

export function FooterLinks({ className = '', footerMenu, showLabel = false }: FooterLinksProps) {
  const theme = useTheme();
  if (!footerMenu?.MenuItems?.length) return null;

  const items = footerMenu.MenuItems.map((item, index) => {
    return <FooterMenuItem key={`footer-root-${index}`} item={item} />;
  });
  return showLabel ? (
    <Box data-component="footer-links" className={className}>
      {showLabel && (
        <Typography
          fontWeight="bold"
          sx={{
            display: 'inline-block',
            color: 'purple.200',
            opacity: '0.5',
            mb: 2,
            mt: 3,
            fontSize: theme.typography.h5.fontSize,
          }}
        >
          {footerMenu.MenuName}
        </Typography>
      )}
      {items}
    </Box>
  ) : (
    items
  );
}
